import axios from 'axios';
import { setLoadingState } from '../redux/db/actions';
import store from '../redux/store';

type LoadingStateKey =
	| 'profile'
	| 'scenes'
	| 'activeScene.frames'
	| 'activeScene.chats'
	| 'guest.scenes'
	| 'guest.frames'
	| 'activeDesignSystemVersion'
	| 'activeDsChat';

export abstract class PollingStream<T> {
	protected aborter: AbortController | null = null;
	protected pollTimer: NodeJS.Timeout | null = null;
	protected lastTimestamp: string | null = null;
	protected isPolling = false;
	protected shouldResumeOnVisible = false;
	private visibilityChangeHandler: (() => void) | null = null;
	private pauseDelayTimer: NodeJS.Timeout | null = null;
	private readonly pauseDelay = 30000; // 30 seconds delay before pausing

	constructor(
		protected url: string,
		protected onError?: (error: Error) => void,
		protected loadingResource?: LoadingStateKey
	) {
		// Set up visibility change listener
		this.visibilityChangeHandler = () => {
			if (document.hidden) {
				// Tab became hidden - schedule pause after delay
				if (this.isPolling && !this.pauseDelayTimer) {
					this.pauseDelayTimer = setTimeout(() => {
						if (this.isPolling) {
							console.log(`[PollingStream] Pausing polling after delay for ${this.url}`);
							this.shouldResumeOnVisible = true;
							this.pausePolling();
						}
						this.pauseDelayTimer = null;
					}, this.pauseDelay);
				}
			} else {
				// Tab became visible - cancel scheduled pause or resume if paused
				if (this.pauseDelayTimer) {
					clearTimeout(this.pauseDelayTimer);
					this.pauseDelayTimer = null;
				} else if (this.shouldResumeOnVisible) {
					console.log(`[PollingStream] Tab visible, resuming polling for ${this.url}`);
					this.shouldResumeOnVisible = false;
					this.resumePolling();
				}
			}
		};

		document.addEventListener('visibilitychange', this.visibilityChangeHandler);
	}

	start(skipLoadingState: boolean = false) {
		this.stop('cleanup'); // Clean up any existing polling

		// Set loading state if resource specified and not skipping
		if (this.loadingResource && !skipLoadingState) {
			store.dispatch(setLoadingState({ resource: this.loadingResource, state: 'loading' }));
		}

		this.aborter = new AbortController();
		this.isPolling = true;

		// Start the initial poll (will return immediately with all data)
		this.poll();
	}

	protected async poll() {
		if (!this.isPolling || !this.aborter) {
			return;
		}

		try {
			// Build URL with timestamp parameter if we have one
			const queryUrl = this.lastTimestamp
				? `${this.url}${this.url.includes('?') ? '&' : '?'}since=${encodeURIComponent(
						this.lastTimestamp
				  )}`
				: this.url;

			const response = await axios.get(queryUrl, {
				withCredentials: true,
				signal: this.aborter.signal,
				headers: {
					'x-studio-hash': import.meta.env.VITE_GIT_SHA || 'development',
				},
			});

			const data = response.data;
			if (!data) {
				// Server returned empty response (e.g. user not yet in DB). Retry
				// after a delay so the polling stream doesn't silently die.
				if (this.isPolling) {
					this.pollTimer = setTimeout(() => this.poll(), 2000);
				}
				return;
			}
			// Let subclass extract data and update timestamp
			const items = this.extractData(data);
			if (data.timestamp && typeof data.timestamp === 'string') {
				this.lastTimestamp = data.timestamp;
			}

			// Always call onData, even with empty arrays, so state can be cleared when needed (e.g. going from a scene with frames, to a scene without frames)
			this.onData(items);

			// Mark as loaded after first successful poll
			if (this.loadingResource) {
				store.dispatch(setLoadingState({ resource: this.loadingResource, state: 'loaded' }));
			}

			// Schedule next poll immediately (server does the long polling)
			if (this.isPolling) {
				this.pollTimer = setTimeout(() => this.poll(), 0);
			}
		} catch (error) {
			if (error instanceof Error) {
				// Ignore abort errors (they're expected when stopping)
				if (error.name === 'AbortError' || error.name === 'CanceledError') {
					return;
				}

				// Handle axios errors
				if (axios.isAxiosError(error)) {
					if (error.response?.status === 403) {
						// Access denied - call onError if provided
						const accessError = new Error('Access denied');
						if (this.onError) {
							this.onError(accessError);
						}
						this.stop('Access denied');
						return;
					}
				}

				console.error('Polling error:', error);
				if (this.onError) {
					this.onError(error);
				}

				// Retry after a delay on error (unless stopped)
				if (this.isPolling) {
					this.pollTimer = setTimeout(() => this.poll(), 5000); // 5 second delay on error
				}
			}
		}
	}

	protected pausePolling() {
		// Abort any in-flight request
		this.aborter?.abort('Tab hidden');
		this.aborter = null;

		// Clear the poll timer
		if (this.pollTimer) {
			clearTimeout(this.pollTimer);
			this.pollTimer = null;
		}

		// Keep isPolling=true and lastTimestamp so we can resume
	}

	protected resumePolling() {
		// Create a new aborter and immediately start polling
		this.aborter = new AbortController();
		this.poll();
	}

	stop(reason: string = 'Stream stopped') {
		this.isPolling = false;
		this.shouldResumeOnVisible = false;
		this.aborter?.abort(reason);
		this.aborter = null;

		if (this.pollTimer) {
			clearTimeout(this.pollTimer);
			this.pollTimer = null;
		}

		// Clear pause delay timer if any
		if (this.pauseDelayTimer) {
			clearTimeout(this.pauseDelayTimer);
			this.pauseDelayTimer = null;
		}

		this.lastTimestamp = null;

		// Only remove visibility listener if truly stopping (not during cleanup/restart)
		// The listener should persist for the lifetime of the PollingStream instance
		if (reason !== 'cleanup' && this.visibilityChangeHandler) {
			document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
			this.visibilityChangeHandler = null;
		}
	}

	// Abstract methods for subclasses to implement
	abstract extractData(response: any): T[];
	abstract onData(data: T[]): void;
}
